import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, Calendar, Clock, ExternalLink } from "lucide-react";
import { Typography } from "@/components/ui/atoms/Typography";
import { StatusPill } from "@/components/ui/atoms/StatusPill";
import { dataTableSortHeader } from "@/components/ui/molecules/DataTable";
import type { InterviewLinkDetail } from "@/types/interview.types";
import { getInterviewStatusVariant } from "@/components/ui/atoms/StatusPill/statusPill.utils";
import { IconWrapper } from "@/components/ui/atoms/IconWrapper";
import { Button } from "@/components/ui/atoms/Button";
import { Link } from "@/components/ui/link";

export interface InterviewLinkTableRow {
  id: string;
  jobTitle: string;
  companyName: string;
  creatorEmail: string;
  candidateName: string;
  candidateEmail: string;
  statusDisplay: string;
  statusRaw: string;
  expiresAtLabel: string;
  expiresExpired: boolean;
  hasToken: boolean;
  invitationToken: string | null;
  link: InterviewLinkDetail;
}

export interface InterviewLinkTableRowOptions {
  formatDate: (dateString: string | null) => string;
  isExpired: (dateString: string | null) => boolean;
}

export function mapInterviewLinkToRow(
  link: InterviewLinkDetail,
  { formatDate, isExpired }: InterviewLinkTableRowOptions
): InterviewLinkTableRow {
  return {
    id: link.interview_id,
    jobTitle: link.job_title || "",
    companyName: link.company_name || "",
    creatorEmail: link.creator_email || "",
    candidateName: link.candidate_name || "",
    candidateEmail: link.candidate_email || "",
    statusDisplay: link.status?.replace(/_/g, " ") || "Unknown",
    statusRaw: link.status || "",
    expiresAtLabel: formatDate(link.expires_at),
    expiresExpired: isExpired(link.expires_at),
    hasToken: Boolean(link.invitation_token),
    invitationToken: link.invitation_token,
    link,
  };
}

export interface InterviewLinksTableColumnsOptions {
  getInterviewUrl: (token: string | null) => string;
  onExtend: (interviewId: string, candidateName: string, currentExpiration: string | null) => void;
}

export function createInterviewLinksTableColumns({
  getInterviewUrl,
  onExtend,
}: InterviewLinksTableColumnsOptions): ColumnDef<InterviewLinkTableRow>[] {
  return [
    {
      accessorKey: "companyName",
      meta: { label: "Company" },
      header: dataTableSortHeader("Company"),
      size: 200,
      minSize: 160,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <Typography variant="body-sm" weight="semibold" color="primary">
            {row.original.link.company_name || "N/A"}
          </Typography>
          <Typography variant="body-sm" weight="medium" color="muted">
            {row.original.link.creator_email || "N/A"}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: "jobTitle",
      meta: { label: "Job title" },
      header: dataTableSortHeader("Job title"),
      size: 200,
      minSize: 160,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="primary">
          {row.original.link.job_title || "N/A"}
        </Typography>
      ),
    },
    {
      accessorKey: "candidateName",
      meta: { label: "Candidate" },
      header: dataTableSortHeader("Candidate"),
      size: 180,
      minSize: 140,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <Typography variant="body-sm" weight="semibold" color="primary">
            {row.original.link.candidate_name || "N/A"}
          </Typography>
          <Typography variant="body-sm" weight="medium" color="muted">
            {row.original.link.candidate_email || "N/A"}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: "statusDisplay",
      meta: { label: "Status" },
      header: dataTableSortHeader("Status"),
      size: 130,
      minSize: 100,
      cell: ({ row }) => (
        <StatusPill
          variant={getInterviewStatusVariant(row.original.statusRaw)}
        >
          {row.original.statusDisplay}
        </StatusPill>
      ),
    },
    {
      accessorKey: "expiresAtLabel",
      meta: { label: "Expires at" },
      header: dataTableSortHeader("Expires at"),
      size: 200,
      minSize: 168,
      cell: ({ row }) => {
        const expired = row.original.expiresExpired;
        return (
          <div
            className={`flex flex-col items-start gap-1.5 ${expired ? "text-red" : "text-dark-invirtus/70"}`}
          >
            <div className="flex items-center gap-1.5">
              <IconWrapper size="xs"><Calendar /></IconWrapper>
              <Typography variant="body-xs" weight="medium" color={expired ? "error" : "secondary"}>
                {row.original.expiresAtLabel}
              </Typography>
            </div>
            <div className="flex min-w-0 flex-col">              
              {expired ? (
                <Typography variant="body-xs" weight="bold" color="error" className="flex items-center gap-1">
                  <IconWrapper size="xs"><AlertCircle /></IconWrapper>
                  Expired
                </Typography>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      id: "link",
      meta: { label: "Link" },
      header: () => (
        <span className="block whitespace-normal break-words text-center font-heading text-xs font-bold uppercase leading-snug tracking-wider text-dark-invirtus">
          Link
        </span>
      ),
      size: 100,
      minSize: 88,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) =>
        row.original.hasToken && row.original.invitationToken ? (
          <div className="flex justify-center">
            <Link
              label="Open"
              href={getInterviewUrl(row.original.invitationToken)}
              target="_blank"
              rel="noopener noreferrer"
              icon={<IconWrapper size="sm"><ExternalLink /></IconWrapper>}
              className="group inline-flex items-center gap-1.5 rounded-full bg-light-blue !px-2.5 !py-1 text-xs font-semibold text-dark-invirtus transition-all duration-200 hover:from-blue/30 hover:to-light-blue/40 hover:shadow-sm hover:border-none"
            />
          </div>
        ) : (
          <Typography variant="caption" className="text-dark-invirtus/40">
            N/A
          </Typography>
        ),
    },
    {
      id: "actions",
      meta: { label: "Actions" },
      header: () => (
        <span className="block whitespace-normal break-words text-center font-heading text-xs font-bold uppercase leading-snug tracking-wider text-dark-invirtus">
          Actions
        </span>
      ),
      size: 110,
      minSize: 96,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const l = row.original.link;
        return (
          <div className="flex justify-center">
            <Button type="contained"
              intent="success-soft"
              size="xs"
              label="Extend"
              onClick={() => onExtend(l.interview_id, l.candidate_name || "Unknown", l.expires_at)}
              icon={<Clock />}
            />
          </div>
        );
      },
    },
  ];
}
